/**
 * Giving somebody an account.
 *
 * The password is suggested rather than demanded. Whoever is doing this is
 * standing next to the new person and will read it out, so it needs to be
 * something sayable — a password nobody can repeat is one that ends up written
 * on the till.
 */
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { api } from '../../lib/api.js';
import { suggestPassword } from './staff.js';
import { RolePicker } from './RolePicker.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Field } from '../../components/ui/Field.jsx';

export function HireSheet({ roles, onClose, onHired }) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState(suggestPassword);
  const [roleIds, setRoleIds] = useState([]);

  const hire = useMutation({
    mutationFn: (body) => api.post('/admin/staff', body),
    onSuccess: onHired,
  });

  const valid =
    firstName.trim() &&
    lastName.trim() &&
    email.includes('@') &&
    password.length >= 8 &&
    roleIds.length;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Add someone"
      className="fixed inset-0 z-30 flex items-start justify-center overflow-y-auto bg-black/70 p-4"
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          hire.mutate({
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            email: email.trim().toLowerCase(),
            phone: phone.trim() || undefined,
            password,
            roleIds,
          });
        }}
        className="w-full max-w-lg rounded-2xl bg-surface p-5"
      >
        <h2 className="font-semibold text-lg text-ink">Add someone</h2>

        <div className="mt-4 grid grid-cols-2 gap-3">
          <Field
            label="First name"
            required
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
          <Field
            label="Last name"
            required
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
        </div>

        <Field
          className="mt-4"
          label="Email"
          type="email"
          autoCapitalize="none"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="name@foodrun.pk"
          hint="This is what they sign in with."
        />

        <Field
          className="mt-4"
          label="Phone (optional)"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
        />

        <div className="mt-4">
          <label htmlFor="new-password" className="mb-1.5 block text-sm font-medium text-ink-2">
            First password
          </label>
          <div className="flex gap-2">
            <input
              id="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="h-12 min-w-0 flex-1 rounded-lg border border-rule bg-surface-3 px-3.5 font-mono text-[15px] text-ink focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
            />
            <Button
              type="button"
              variant="secondary"
              onClick={() => setPassword(suggestPassword())}
            >
              Another
            </Button>
          </div>
          <p className="mt-1.5 text-xs text-ink-3">
            Read this out to them. They can change it once they are signed in.
          </p>
        </div>

        <div className="mt-4">
          <RolePicker roles={roles} chosen={roleIds} onChange={setRoleIds} />
        </div>

        {hire.error && (
          <p role="alert" className="mt-4 rounded-lg bg-bad-bg px-3.5 py-2.5 text-sm text-bad">
            {hire.error.message}
          </p>
        )}

        <div className="mt-5 flex gap-2">
          <Button type="button" variant="secondary" size="lg" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            size="lg"
            className="flex-1"
            busy={hire.isPending}
            disabled={!valid}
          >
            Create the account
          </Button>
        </div>
      </form>
    </div>
  );
}
